/**
 * KAN-648: ячейка таблицы аккаунтов: видеозапись (файл и/или ссылка, открывается
 * в новой вкладке) и бейдж признака 2FA при создании ("2FA" / "без 2FA" / "?").
 * Видео выдаёт сервер после проверки прав: presigned адрес S3 или сам файл
 * (KAN-785, локальное хранилище: /media на проде не раздаётся, нужен токен),
 * поэтому вкладка открывается сразу по клику и получает адрес после ответа.
 */
import { Film, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { twoFaBadge, type VideoSource } from '@/lib/accountVideo';
import { describeApiError } from '@/lib/telegramConnect';
import { revokeObjectUrlOnClose } from '@/services/api/apiFiles';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const ICON_LINK =
  'inline-flex h-6 w-6 items-center justify-center rounded text-blue-600 hover:bg-blue-50 hover:text-blue-800';

/**
 * Адрес для вкладки. Blob-адрес живёт, пока открыта вкладка: плеер докачивает
 * видео range-запросами, после отзыва сломалась бы перемотка.
 */
function videoHref(source: VideoSource, tab: Window | null): string {
  if (source.kind === 'url') return source.url;
  const objectUrl = URL.createObjectURL(source.blob);
  revokeObjectUrlOnClose(tab, objectUrl);
  return objectUrl;
}

/** Текст в пустой вкладке, пока файл качается; about:blank может быть недоступен, тогда молча. */
function showLoading(tab: Window | null): void {
  try {
    const body = tab?.document?.body;
    if (body) body.textContent = 'Загрузка видео...';
  } catch {
    // вкладка не наша или уже ушла: оставить как есть
  }
}

async function openVideoFile(account: TelegramAccount): Promise<void> {
  // Вкладка открывается в обработчике клика, иначе блокировщик всплывающих окон её закроет.
  const tab = window.open('', '_blank');
  showLoading(tab);
  try {
    const source = await telegramAccountsApi.videoFile(
      account.account_id,
      account.operator_id
    );
    const href = videoHref(source, tab);
    if (tab) {
      tab.opener = null;
      tab.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  } catch (err) {
    tab?.close();
    toast.error(describeApiError(err, 'Не удалось открыть видео'));
  }
}

export function AccountVideoBadges({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element {
  const key = `${account.operator_id}-${account.account_id}`;
  const badge = twoFaBadge(account.has_2fa_at_creation);
  const url = (account.video_url ?? '').trim();
  return (
    <div className="flex items-center gap-1" data-testid={`video-cell-${key}`}>
      {account.has_video_file && (
        <button
          type="button"
          className={ICON_LINK}
          title="Открыть файл видео"
          aria-label="Открыть файл видео"
          data-testid={`video-file-${key}`}
          onClick={() => void openVideoFile(account)}
        >
          <Film className="h-4 w-4" />
        </button>
      )}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={ICON_LINK}
          title="Открыть ссылку на видео"
          aria-label="Открыть ссылку на видео"
          data-testid={`video-url-${key}`}
        >
          <Link2 className="h-4 w-4" />
        </a>
      )}
      <span
        className={`rounded px-1.5 py-0.5 text-xs font-medium ${badge.className}`}
        title={badge.title}
        data-testid={`two-fa-${key}`}
      >
        {badge.label}
      </span>
    </div>
  );
}
